import { useState, useMemo } from 'react';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import type { CreateMaterialInput } from '@/services/admin';

interface MaterialFormProps {
  readonly onSubmit: (data: CreateMaterialInput) => void;
  readonly onCancel: () => void;
  readonly isPending: boolean;
}

const DEFAULT_DENSITY = 1.24;
const MARKUP_MULTIPLIER = 3;

const INITIAL_STATE = {
  name: '',
  description: '',
  spoolCost: '',
  spoolWeightGrams: '1000',
  densityGPerCm3: String(DEFAULT_DENSITY),
  estimatedDaysBase: '3',
  colorsText: '',
};

const calculateEstimatedRate = (
  spoolCost: number,
  spoolWeightGrams: number,
  density: number,
): number => {
  const costPerGram = spoolCost / spoolWeightGrams;
  const costPerCm3 = costPerGram * density;
  return Math.round(costPerCm3 * MARKUP_MULTIPLIER * 1000) / 1000;
};

export const MaterialForm = ({ onSubmit, onCancel, isPending }: MaterialFormProps) => {
  const [form, setForm] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const pricePreview = useMemo(() => {
    const cost = Number(form.spoolCost);
    const weight = Number(form.spoolWeightGrams);
    const density = Number(form.densityGPerCm3);

    if (!cost || !weight || !density || cost <= 0 || weight <= 0 || density <= 0) {
      return null;
    }

    const rate = calculateEstimatedRate(cost, weight, density);
    const costPerGram = cost / weight;
    const breakEvenGrams = Math.round(weight / MARKUP_MULTIPLIER);

    return { rate, costPerGram, breakEvenGrams };
  }, [form.spoolCost, form.spoolWeightGrams, form.densityGPerCm3]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!form.name.trim()) newErrors.name = 'Required';
    if (!form.description.trim()) newErrors.description = 'Required';

    const cost = Number(form.spoolCost);
    if (!form.spoolCost || isNaN(cost) || cost <= 0) newErrors.spoolCost = 'Must be a positive number';

    const weight = Number(form.spoolWeightGrams);
    if (!form.spoolWeightGrams || isNaN(weight) || weight <= 0) newErrors.spoolWeightGrams = 'Must be a positive number';

    const density = Number(form.densityGPerCm3);
    if (!form.densityGPerCm3 || isNaN(density) || density <= 0) newErrors.densityGPerCm3 = 'Must be a positive number';

    const days = Number(form.estimatedDaysBase);
    if (!form.estimatedDaysBase || isNaN(days) || !Number.isInteger(days) || days < 1) {
      newErrors.estimatedDaysBase = 'Must be a positive integer';
    }

    const colors = form.colorsText
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);
    if (colors.length === 0) newErrors.colorsText = 'At least one color required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const colors = form.colorsText
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);

    const density = Number(form.densityGPerCm3);

    onSubmit({
      name: form.name.trim(),
      description: form.description.trim(),
      defaultSpoolCost: Number(form.spoolCost),
      defaultSpoolWeightGrams: Number(form.spoolWeightGrams),
      densityGPerCm3: density,
      colorOptions: colors,
      estimatedDaysBase: Number(form.estimatedDaysBase),
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div
        style={{
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--color-primary-200)',
          backgroundColor: 'var(--bg-surface)',
          padding: 'var(--space-6)',
          marginBottom: 'var(--space-6)',
        }}
      >
        <h3
          style={{
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--text-primary)',
            marginBottom: 'var(--space-4)',
          }}
        >
          New Material
        </h3>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))',
            gap: 'var(--space-4)',
            marginBottom: 'var(--space-4)',
          }}
        >
          <Input
            id="name"
            label="Material Name"
            placeholder="e.g. PLA Basic"
            value={form.name}
            onChange={(e) => updateField('name', e.target.value)}
            error={errors.name}
          />
          <Input
            id="spoolCost"
            label="Spool Cost ($)"
            type="number"
            step="0.01"
            min="0"
            placeholder="19.99"
            value={form.spoolCost}
            onChange={(e) => updateField('spoolCost', e.target.value)}
            error={errors.spoolCost}
          />
          <Input
            id="spoolWeightGrams"
            label="Spool Weight (grams)"
            type="number"
            min="1"
            placeholder="1000"
            value={form.spoolWeightGrams}
            onChange={(e) => updateField('spoolWeightGrams', e.target.value)}
            error={errors.spoolWeightGrams}
          />
          <Input
            id="densityGPerCm3"
            label="Density (g/cm3)"
            type="number"
            step="0.01"
            min="0.1"
            placeholder="1.24"
            value={form.densityGPerCm3}
            onChange={(e) => updateField('densityGPerCm3', e.target.value)}
            error={errors.densityGPerCm3}
          />
          <Input
            id="estimatedDaysBase"
            label="Estimated Days"
            type="number"
            min="1"
            max="30"
            placeholder="3"
            value={form.estimatedDaysBase}
            onChange={(e) => updateField('estimatedDaysBase', e.target.value)}
            error={errors.estimatedDaysBase}
          />
        </div>

        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Input
            id="description"
            label="Description"
            placeholder="Brief description of the material"
            value={form.description}
            onChange={(e) => updateField('description', e.target.value)}
            error={errors.description}
          />
        </div>

        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Input
            id="colorsText"
            label="Colors (comma-separated)"
            placeholder="White, Black, Grey, Red"
            value={form.colorsText}
            onChange={(e) => updateField('colorsText', e.target.value)}
            error={errors.colorsText}
          />
        </div>

        {/* Pricing Preview */}
        {pricePreview && (
          <div
            style={{
              padding: 'var(--space-3) var(--space-4)',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--bg-accent-subtle)',
              marginBottom: 'var(--space-4)',
              fontSize: 'var(--text-sm)',
              color: 'var(--text-secondary)',
            }}
          >
            <p style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)', marginBottom: 'var(--space-1)' }}>
              Pricing Preview
            </p>
            <p>Rate: ${pricePreview.rate.toFixed(3)}/cm3 (3x markup)</p>
            <p>Raw cost: ${pricePreview.costPerGram.toFixed(4)}/g</p>
            <p>Break-even at ~{pricePreview.breakEvenGrams}g used ({Math.round(100 / MARKUP_MULTIPLIER)}% of spool)</p>
          </div>
        )}

        <div className="flex" style={{ gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
          <Button type="button" variant="ghost" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? 'Creating...' : 'Create Material'}
          </Button>
        </div>
      </div>
    </form>
  );
};
